import { useEffect, useState } from 'react';
import { api } from '../../lib/api';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Label } from '../../components/ui/label';
import { Card } from '../../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { Switch } from '../../components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { toast } from 'sonner';
import { Plus, Pencil, Trash2, Loader2, Save, Percent, Tag, BadgePercent } from 'lucide-react';

const inputCls = 'mt-1.5 bg-white/5 border-white/15 text-white placeholder:text-white/35';

const empty = {
  code: '',
  discount_type: 'percent',
  discount_value: 10,
  scope: 'shop',
  min_order_aed: 0,
  max_uses: 0,
  active: true,
  starts_at: '',
  expires_at: '',
  description: '',
};

export default function PromoCodesTab() {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/promo');
      setItems(data || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to load promo codes');
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    setSaving(true);
    try {
      const payload = { ...editing };
      delete payload.created_at;
      delete payload.updated_at;
      delete payload.uses_count;
      // sanitize empty date strings
      if (!payload.starts_at) delete payload.starts_at;
      if (!payload.expires_at) delete payload.expires_at;
      if (editing.id) {
        await api.patch(`/promo/${editing.id}`, payload);
        toast.success('Promo code updated');
      } else {
        await api.post('/promo', payload);
        toast.success('Promo code created');
      }
      setOpen(false);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const del = async (it) => {
    if (!window.confirm(`Delete promo code "${it.code}"?`)) return;
    try {
      await api.delete(`/promo/${it.id}`);
      toast.success('Deleted');
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Delete failed');
    }
  };

  const fmtDiscount = (it) => {
    if (it.discount_type === 'percent') return `−${it.discount_value}%`;
    return `−AED ${Number(it.discount_value).toFixed(0)}`;
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-5">
        <div>
          <div className="font-display text-xl font-semibold">Promo codes</div>
          <div className="text-xs text-white/55">Discount codes customers can enter at checkout</div>
        </div>
        <Button
          data-testid="admin-add-promo-button"
          onClick={() => { setEditing({ ...empty }); setOpen(true); }}
          className="bg-[#c8e600] text-[#0a1628] hover:bg-[#b6d100] rounded-xl"
        >
          <Plus className="mr-1 h-4 w-4" /> Add code
        </Button>
      </div>

      {loading ? (
        <div className="rounded-3xl border border-white/10 bg-white/5 p-10 text-center text-white/55">
          <Loader2 className="mx-auto h-6 w-6 animate-spin" />
        </div>
      ) : items.length === 0 ? (
        <div className="rounded-3xl border border-white/10 bg-white/5 p-10 text-center text-white/55">
          <BadgePercent className="mx-auto h-8 w-8 text-white/30 mb-2" />
          No promo codes yet. Create one to give customers a discount at checkout.
        </div>
      ) : (
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {items.map((it) => (
            <Card key={it.id} className="rounded-2xl border-white/10 bg-white/5 p-4">
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2">
                    <Tag className="h-4 w-4 text-[#00b4d8] shrink-0" />
                    <div className="font-mono text-base font-semibold tracking-wider truncate">{it.code}</div>
                    {!it.active && (
                      <span className="text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded-full bg-white/10 text-white/60 border border-white/15">
                        Inactive
                      </span>
                    )}
                  </div>
                  <div className="mt-1 text-lg font-display font-semibold text-[#c8e600]">{fmtDiscount(it)}</div>
                  {it.description && <div className="mt-1 text-xs text-white/55 line-clamp-2">{it.description}</div>}
                  <div className="mt-2 flex flex-wrap gap-x-3 gap-y-1 text-[11px] text-white/50">
                    {it.min_order_aed > 0 && <span>Min order AED {it.min_order_aed}</span>}
                    <span>Used {it.uses_count || 0}{it.max_uses ? ` / ${it.max_uses}` : ''}</span>
                    {it.expires_at && <span>Expires {new Date(it.expires_at).toLocaleDateString()}</span>}
                  </div>
                </div>
                <div className="flex flex-col gap-1 shrink-0">
                  <Button size="sm" variant="outline" onClick={() => { setEditing(it); setOpen(true); }} className="border-white/15 bg-white/5 text-white hover:bg-white/10 hover:text-white rounded-lg h-8">
                    <Pencil className="h-3.5 w-3.5" />
                  </Button>
                  <Button size="sm" variant="ghost" onClick={() => del(it)} className="text-red-400 hover:text-red-200 hover:bg-red-500/10 h-8">
                    <Trash2 className="h-3.5 w-3.5" />
                  </Button>
                </div>
              </div>
            </Card>
          ))}
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg bg-[#07101f] border-white/12 text-white">
          <DialogHeader><DialogTitle className="font-display text-xl">{editing?.id ? 'Edit promo code' : 'New promo code'}</DialogTitle></DialogHeader>
          {editing && (
            <div className="space-y-4 max-h-[70vh] overflow-y-auto scrollbar-thin pr-1">
              <div>
                <Label>Code</Label>
                <Input
                  data-testid="admin-promo-code-input"
                  className={`${inputCls} font-mono uppercase tracking-wider`}
                  placeholder="WELCOME10"
                  value={editing.code}
                  onChange={(e) => setEditing({ ...editing, code: e.target.value.toUpperCase() })}
                />
                <div className="text-[11px] text-white/45 mt-1">Case-insensitive at checkout, but stored upper-case.</div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Discount type</Label>
                  <Select value={editing.discount_type} onValueChange={(v) => setEditing({ ...editing, discount_type: v })}>
                    <SelectTrigger className={inputCls}><SelectValue /></SelectTrigger>
                    <SelectContent className="bg-[#07101f] border-white/15 text-white">
                      <SelectItem value="percent" className="focus:bg-white/10 focus:text-white">Percent (%)</SelectItem>
                      <SelectItem value="fixed" className="focus:bg-white/10 focus:text-white">Fixed AED</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Value</Label>
                  <div className="relative">
                    <Input
                      data-testid="admin-promo-value-input"
                      className={`${inputCls} pr-10`}
                      type="number"
                      min={0}
                      step="1"
                      value={editing.discount_value ?? 0}
                      onChange={(e) => setEditing({ ...editing, discount_value: e.target.value === '' ? '' : Number(e.target.value) })}
                    />
                    <div className="absolute right-3 top-1/2 -translate-y-1/2 text-white/45 text-sm pointer-events-none">
                      {editing.discount_type === 'percent' ? '%' : 'AED'}
                    </div>
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Minimum order (AED)</Label>
                  <Input
                    className={inputCls}
                    type="number"
                    min={0}
                    step="1"
                    value={editing.min_order_aed ?? 0}
                    onChange={(e) => setEditing({ ...editing, min_order_aed: e.target.value === '' ? 0 : Number(e.target.value) })}
                  />
                </div>
                <div>
                  <Label>Max uses (0 = unlimited)</Label>
                  <Input
                    className={inputCls}
                    type="number"
                    min={0}
                    step="1"
                    value={editing.max_uses ?? 0}
                    onChange={(e) => setEditing({ ...editing, max_uses: e.target.value === '' ? 0 : Number(e.target.value) })}
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Starts at (optional)</Label>
                  <Input
                    className={inputCls}
                    type="datetime-local"
                    value={editing.starts_at ? editing.starts_at.slice(0, 16) : ''}
                    onChange={(e) => setEditing({ ...editing, starts_at: e.target.value ? new Date(e.target.value).toISOString() : '' })}
                  />
                </div>
                <div>
                  <Label>Expires at (optional)</Label>
                  <Input
                    className={inputCls}
                    type="datetime-local"
                    value={editing.expires_at ? editing.expires_at.slice(0, 16) : ''}
                    onChange={(e) => setEditing({ ...editing, expires_at: e.target.value ? new Date(e.target.value).toISOString() : '' })}
                  />
                </div>
              </div>

              <div>
                <Label>Internal note (optional)</Label>
                <Textarea rows={2} className={inputCls} placeholder="Where you use this code, campaign name, etc." value={editing.description} onChange={(e) => setEditing({ ...editing, description: e.target.value })} />
              </div>

              <div className="flex items-center gap-3 pt-2 border-t border-white/10">
                <Switch checked={editing.active} onCheckedChange={(v) => setEditing({ ...editing, active: v })} />
                <Label>Active — customers can use this code</Label>
              </div>
            </div>
          )}
          <div className="flex justify-end gap-2 mt-2">
            <Button variant="ghost" onClick={() => setOpen(false)} className="text-white/65 hover:text-white hover:bg-white/5">Cancel</Button>
            <Button
              data-testid="admin-promo-save"
              onClick={save}
              disabled={saving || !editing?.code || !editing?.discount_value}
              className="bg-[#c8e600] text-[#0a1628] hover:bg-[#b6d100]"
            >
              {saving ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Saving</> : <><Save className="mr-2 h-4 w-4" /> Save</>}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
